import cn from 'classnames'
import React, { FC, HTMLAttributes, useCallback, useEffect, useMemo, useState } from 'react'
import { usePrevious } from 'react-use'

import styles from './Carousel.module.scss'
import CarouselContent from './CarouselContent'
import CarouselControls from './CarouselControls'
import CarouselItem from './CarouselItem'
import { CarouselContext } from './utils'

import { stopPropagation } from '~lib/helpers'

interface CarouselProps extends HTMLAttributes<HTMLDivElement> {
  /**
   * Children elements
   */
  children?: React.ReactNode
  /**
   * Classname for extra styles
   */
  className?: string
  /**
   * Idx no of the initial slide to display
   */
  initialSlide?: number
  /**
   * Callback to run before changing the slide index
   */
  onBeforeChange?: (currentSlide: number, nextSlide: number) => void
}

// TODO: Add smooth animations
const Carousel: FC<React.PropsWithChildren<CarouselProps>> = ({
  children,
  className,
  initialSlide,
  onBeforeChange,
  ...props
}) => {
  const [selectedItem, setSelectedItem] = useState<number>(initialSlide || 0)
  const prevItem = usePrevious(selectedItem)

  const [itemCount, setItemCount] = useState<number>(React.Children.count(children))

  const moveToRight = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      stopPropagation(e)
      setSelectedItem((prev) => (prev === itemCount - 1 ? 0 : prev + 1))
    },
    [itemCount]
  )

  const moveToLeft = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      stopPropagation(e)
      setSelectedItem((prev) => (prev === 0 ? itemCount - 1 : prev - 1))
    },
    [itemCount]
  )

  const value = useMemo(
    () => ({ selectedItem, moveToRight, moveToLeft, itemCount, setItemCount }),
    [itemCount, moveToLeft, moveToRight, selectedItem]
  )

  useEffect(() => onBeforeChange?.(prevItem, selectedItem), [onBeforeChange, prevItem, selectedItem])

  return (
    <CarouselContext.Provider value={value}>
      <div className={cn(styles.root, className)} {...props}>
        {children}
      </div>
    </CarouselContext.Provider>
  )
}

export default Object.assign(Carousel, {
  Item: CarouselItem,
  Content: CarouselContent,
  Controls: CarouselControls,
})
